import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LogIn, LogOut, UserPlus, X, Badge as BadgeIcon } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast, PageHeader, Button, Input, EmptyState } from '../components/ui';
import { fmtMoney } from '../utils/currency';

const EMPTY_FORM = { userId: '', employeeId: '', position: '', salary: '' };

const Staff = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const canManage = ['super_admin', 'admin', 'manager'].includes(user?.role);

  useEffect(() => {
    fetchStaff();
    if (canManage) fetchUsers();
  }, [user]);

  const fetchStaff = async () => {
    try {
      const res = await axios.get('/api/staff', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setStaff(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/api/users', { headers: { 'x-auth-token': localStorage.getItem('token') } });
      setUsers(res.data.filter(u => u.role !== 'admin'));
    } catch (err) { console.error(err); }
  };

  // Users who don't already have a Staff record — the only ones that make
  // sense to pick when creating a new one.
  const linkedUserIds = new Set(staff.map(s => s.user?._id || s.user));
  const availableUsers = users.filter(u => !linkedUserIds.has(u._id));

  const openAddModal = () => {
    setForm({ ...EMPTY_FORM, userId: availableUsers[0]?._id || '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userId) {
      toast.error('Select a user to link this staff record to');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/api/staff', {
        userId: form.userId,
        employeeId: form.employeeId || undefined,
        position: form.position || undefined,
        salary: form.salary === '' ? undefined : Number(form.salary),
      }, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('Staff record created');
      closeModal();
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error creating staff record');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClockIn = async () => {
    try {
      await axios.post('/api/staff/clock-in', {}, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('Clocked in');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error clocking in');
    }
  };

  const handleClockOut = async () => {
    try {
      await axios.post('/api/staff/clock-out', {}, { headers: { 'x-auth-token': localStorage.getItem('token') } });
      toast.success('Clocked out');
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Error clocking out');
    }
  };

  return (
    <>
      <PageHeader
        title="Staff"
        subtitle="Records, attendance, and clock-in / clock-out."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="success" leftIcon={<LogIn size={16} />} onClick={handleClockIn}>
              Clock In
            </Button>
            <Button variant="danger" leftIcon={<LogOut size={16} />} onClick={handleClockOut}>
              Clock Out
            </Button>
            {canManage && (
              <Button variant="primary" leftIcon={<UserPlus size={16} />} onClick={openAddModal}>
                Add Staff
              </Button>
            )}
          </div>
        }
      />

      <div className="bg-white rounded-lg border border-neutral-200 shadow-card overflow-x-auto">
        <table className="app-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Position</th>
              <th>Employee ID</th>
              <th>Salary</th>
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 && (
              <tr>
                <td colSpan="4">
                  <EmptyState
                    icon={BadgeIcon}
                    title="No staff records yet"
                    message={canManage ? 'Click "Add Staff" to create the first one.' : 'Ask an admin to add staff records.'}
                  />
                </td>
              </tr>
            )}
            {staff.map(s => (
              <tr key={s._id}>
                <td className="font-medium">{s.user?.name}</td>
                <td className="text-neutral-600">{s.position || <span className="text-neutral-400">—</span>}</td>
                <td className="text-neutral-600">{s.employeeId || <span className="text-neutral-400">—</span>}</td>
                <td className="tabular-nums">{s.salary ? fmtMoney(s.salary, true) : <span className="text-neutral-400">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add staff modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-card-hover w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-neutral-900">Add staff record</h2>
              <button type="button" onClick={closeModal} className="text-neutral-400 hover:text-neutral-700 transition-colors">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm font-medium text-neutral-700 block mb-1">User</label>
                  {availableUsers.length === 0 ? (
                    <p className="text-sm text-warning-700 bg-warning-50 border border-warning-200 rounded-md p-3">
                      Every existing user already has a staff record. Create a new user first
                      (User Management), then come back here.
                    </p>
                  ) : (
                    <select
                      value={form.userId}
                      onChange={(e) => setForm({ ...form, userId: e.target.value })}
                      className="w-full border border-neutral-300 rounded-md bg-white px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-primary-200 focus:border-primary-500"
                      required
                    >
                      <option value="">Select a user…</option>
                      {availableUsers.map(u => (
                        <option key={u._id} value={u._id}>{u.name} ({u.email}) — {u.role}</option>
                      ))}
                    </select>
                  )}
                </div>

                <Input
                  label="Employee ID (optional)"
                  type="text"
                  placeholder="e.g. EMP-001"
                  value={form.employeeId}
                  onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                />
                <Input
                  label="Position (optional)"
                  type="text"
                  placeholder="e.g. Line Cook"
                  value={form.position}
                  onChange={(e) => setForm({ ...form, position: e.target.value })}
                />
                <Input
                  label="Monthly salary (optional)"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={form.salary}
                  onChange={(e) => setForm({ ...form, salary: e.target.value })}
                  helper="Used to calculate hourly rate for payroll."
                />
              </div>

              <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex gap-2 sticky bottom-0">
                <Button type="button" variant="secondary" fullWidth onClick={closeModal}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" fullWidth loading={submitting} disabled={availableUsers.length === 0}>
                  Add staff
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Staff;
